import React, { useState, useMemo, useEffect } from "react";
import {
  dayKey, subscribeRoster, subscribeDay, commitManualLocation,
} from "../utils/phoneCheckIn";

/**
 * King's Academy — Cubby Check-In (manual, for the Dean of Students)
 *
 * A stand-in for the scanners until they ship. Reads the `students` collection,
 * parses each student's `cubby` (e.g. "SE-3" = Sulafat East, cubby 3) into a
 * location and slot, and lays students out by location. As each location's
 * report comes in, the dean clicks the cubbies that are MISSING a phone, then
 * commits the location. Unclicked cubbies are recorded as turned in; clicked
 * ones stay not-turned-in.
 *
 * Phone-exempt students (students/{sisId}.phoneExempt === true — parental
 * permission to leave the phone at home, set in Cubby Manager) are shown greyed
 * out and locked: they cannot be marked missing and are left out of the commit,
 * so no record is written for them.
 *
 * State PERSISTS: the grid reflects what is committed in Firestore, so a refresh
 * or the midnight rollover no longer blanks it.
 *
 * Mobile-friendly: two-column cubby grid on phones, a horizontally scrollable
 * location picker, and a sticky Commit/Clear bar so a teacher reconciling a
 * dorm on their phone never has to scroll back up to act.
 */

const NAVY = "#233C5F";
const RED = "#E83530";
const GOLD = "#A29164";
const BEIGE = "#F5F0E6";

// cubby prefix -> location label + the station id written on the record.
const LOCATIONS = [
  { key: "SE", label: "Sulafat East", station: "sulafat-east" },
  { key: "SW", label: "Sulafat West", station: "sulafat-west" },
  { key: "AL", label: "Alnilam", station: "alnilam" },
  { key: "ME", label: "Meissa", station: "meissa" },
  { key: "MU", label: "Murzim", station: "murzim" },
  { key: "G", label: "Nihal", station: "nihal" },
  { key: "AT", label: "Atair", station: "atair" },
  { key: "G9", label: "Grade 9", station: "grade-9" },
  { key: "G10", label: "Grade 10", station: "grade-10" },
  { key: "G11", label: "Grade 11", station: "grade-11" },
  { key: "G12", label: "Grade 12", station: "grade-12" },
];
const LOC_BY_KEY = Object.fromEntries(LOCATIONS.map((l) => [l.key, l]));
const LOC_BY_STATION = Object.fromEntries(LOCATIONS.map((l) => [l.station, l]));

// Atair is grouped by physical cubby block, mirroring the floor layout.
const ATAIR_GROUPS = [
  { label: "Section 1", rooms: ["123", "122", "103", "121", "120", "119", "118", "104"] },
  { label: "Section 2", rooms: ["137", "124", "134", "135", "136", "128", "127", "126", "125"] },
  { label: "Section 3", rooms: ["G23", "G22", "G06", "G20", "G19", "G04", "G05", "G07", "G08"] },
  { label: "Section 4", rooms: ["G24", "G28", "G27", "G26", "G25", "G37", "G38", "G39"] },
];
const ATAIR_ORDER = ATAIR_GROUPS.flatMap((g) => g.rooms);

// Murzim is grouped into four sections by slot number, mirroring Atair's layout.
const MURZIM_GROUPS = [
  { label: "Section 1", min: 1,  max: 10 },
  { label: "Section 2", min: 11, max: 20 },
  { label: "Section 3", min: 21, max: 30 },
  { label: "Section 4", min: 31, max: Infinity }, // 31 and up
];

// Stations that render as grouped sections instead of one flat grid.
const GROUPED_STATIONS = {
  atair: { kind: "rooms", groups: ATAIR_GROUPS, order: ATAIR_ORDER },
  murzim: { kind: "range", groups: MURZIM_GROUPS },
};

// "SE-3" -> { key:"SE", room:"3" }, "AT-G06" -> { key:"AT", room:"G06" }.
// Splits on the first dash so digit-bearing prefixes (G9..G12) and alphanumeric
// room codes both work. The cubby value sometimes carries a trailing name, e.g.
// "MU-1 FIRSTLAST" — keep only the leading room token before the first space.
function parseCubby(cubby) {
  if (!cubby) return null;
  const s = String(cubby).trim().toUpperCase();
  let key, numStr;
  const dash = s.indexOf("-");
  if (dash > 0) {
    key = s.slice(0, dash);
    numStr = s.slice(dash + 1);
  } else {
    const found = LOCATIONS.map((l) => l.key).sort((a, b) => b.length - a.length).find((k) => s.startsWith(k));
    if (!found) return null;
    key = found;
    numStr = s.slice(found.length);
  }
  const room = (numStr || "").trim().split(/[\s-]+/)[0]; // drop any trailing name (space- or hyphen-joined)
  if (!LOC_BY_KEY[key] || room === "") return null;
  return { key, room };
}

// Sort a location's students: Atair by room order, everyone else numerically
// (falling back to text when a room code isn't numeric).
function sortStudents(key, arr) {
  const station = LOC_BY_KEY[key].station;
  if (station === "atair") {
    const idx = (r) => { const i = ATAIR_ORDER.indexOf(r); return i < 0 ? 1e9 : i; };
    return arr.slice().sort((a, b) => idx(a.room) - idx(b.room));
  }
  return arr.slice().sort((a, b) => {
    const na = Number(a.room), nb = Number(b.room);
    if (!isNaN(na) && !isNaN(nb)) return na - nb;
    return String(a.room).localeCompare(String(b.room));
  });
}

// Same reads as Cubby Manager, so the two never disagree.
const isExemptStudent = (s) => s.phoneExempt === true || String(s.phoneExempt).toLowerCase() === "true";
// Card tag per /students.phonePolicy (set in Cubby Manager).
const EXEMPT_TAGS = { leaveAtHome: "Leaves at home", noPhone: "No phone", withdrawn: "Withdrawn" };
// A student must be POSITIVELY active to appear: `status` is authoritative when
// present ("active" only); if absent, fall back to `active` (true / "true").
// A doc with neither field is treated as inactive.
const isActiveStudent = (s) => {
  const status = String(s.status == null ? "" : s.status).trim().toLowerCase();
  if (status) return status === "active";
  const a = s.active;
  if (a === undefined || a === null || String(a).trim() === "") return false;
  return a === true || String(a).trim().toLowerCase() === "true";
};

export default function CubbyCheckIn() {
  const [rawRoster, setRawRoster] = useState([]);
  const [dayRecords, setDayRecords] = useState({});
  const [today, setToday] = useState(() => dayKey());
  const [operator, setOperator] = useState("");
  const [selected, setSelected] = useState("SE");
  const [missing, setMissing] = useState({});   // studentId -> bool: dean's staged override this session
  const [feedback, setFeedback] = useState(null);

  useEffect(() => subscribeRoster(setRawRoster), []);
  useEffect(() => subscribeDay(today, setDayRecords), [today]);
  useEffect(() => {
    const t = setInterval(() => { const k = dayKey(); if (k !== today) { setToday(k); setDayRecords({}); setMissing({}); } }, 60000);
    return () => clearInterval(t);
  }, [today]);

  // Students grouped by location, each sorted for display.
  const byLocation = useMemo(() => {
    const groups = {};
    rawRoster.forEach((s) => {
      if (!isActiveStudent(s)) return;
      const c = parseCubby(s.cubby);
      if (!c) return;
      const slot = /^\d+$/.test(c.room) ? parseInt(c.room, 10) : c.room;
      (groups[c.key] = groups[c.key] || []).push({
        id: s.id,
        name: [s.firstName, s.lastName].filter(Boolean).join(" ") || s.name || s.id,
        code: `${c.key}-${c.room}`,          // clean display code, no trailing name
        room: c.room,
        station: LOC_BY_KEY[c.key].station,
        slot,
        exempt: isExemptStudent(s),
        exemptNote: s.phoneExemptNote ? String(s.phoneExemptNote) : "",
        exemptTag: EXEMPT_TAGS[String(s.phonePolicy || "")] || "Exempt",
      });
    });
    Object.keys(groups).forEach((key) => { groups[key] = sortStudents(key, groups[key]); });
    return groups;
  }, [rawRoster]);

  const recIsMissing = (rec) => !!rec && rec.status === "missing";
  const recIsIn = (rec) => !!rec && (rec.status === "in" || rec.status === "returned");

  function isMissing(id) {
    if (id in missing) return !!missing[id];
    return recIsMissing(dayRecords[id]);
  }

  const locState = useMemo(() => {
    const committed = {};
    const recorded = {};
    Object.values(dayRecords).forEach((r) => {
      const loc = LOC_BY_STATION[r.station];
      if (!loc) return;
      if (r.source === "dean-manual") committed[loc.key] = true;
      if (recIsIn(r)) recorded[loc.key] = (recorded[loc.key] || 0) + 1;
    });
    return { committed, recorded };
  }, [dayRecords]);

  const list = byLocation[selected] || [];
  // Exempt students are shown for completeness but never counted or committed.
  const checkable = list.filter((s) => !s.exempt);
  const exemptCount = list.length - checkable.length;
  const missingCount = checkable.filter((s) => isMissing(s.id)).length;
  const presentCount = checkable.length - missingCount;

  function toggle(id) {
    const cur = isMissing(id);
    setMissing((m) => ({ ...m, [id]: !cur }));
  }

  function commit() {
    const loc = LOC_BY_KEY[selected];
    if (checkable.length === 0) return;
    if (!window.confirm(`Commit ${loc.label}? ${presentCount} turned in, ${missingCount} reported missing.${exemptCount ? ` (${exemptCount} exempt, not recorded.)` : ""}`)) return;
    const rows = checkable.map((s) => ({ id: s.id, station: s.station, slot: s.slot, turnedIn: !isMissing(s.id) }));
    commitManualLocation(today, rows, { operator })
      .then(() => {
        setMissing((m) => { const n = { ...m }; list.forEach((s) => delete n[s.id]); return n; });
        setFeedback({ kind: "ok", text: `${loc.label} committed: ${presentCount} in, ${missingCount} missing.` });
      })
      .catch((e) => setFeedback({ kind: "err", text: `Commit failed: ${e.message}` }));
  }

  function clearLocation() {
    setMissing((m) => { const n = { ...m }; list.forEach((s) => delete n[s.id]); return n; });
  }

  const renderCubby = (s) => {
    if (s.exempt) {
      return (
        <button
          key={s.id}
          className="cc-cubby exempt"
          disabled
          aria-disabled="true"
          title={`${s.exemptTag}${s.exemptNote ? ` — ${s.exemptNote}` : ""} (not recorded)`}
        >
          <span className="cc-cubby-code">{s.code}</span>
          <span className="cc-cubby-name">{s.name}</span>
          <span className="cc-cubby-id">{s.id}</span>
          <span className="cc-cubby-tag">{s.exemptTag}</span>
        </button>
      );
    }
    const miss = isMissing(s.id);
    const inFs = recIsIn(dayRecords[s.id]);
    const tag = miss ? "Missing" : inFs ? "Turned in" : "In";
    return (
      <button key={s.id} className={`cc-cubby ${miss ? "missing" : ""}`} onClick={() => toggle(s.id)}>
        <span className="cc-cubby-code">{s.code}</span>
        <span className="cc-cubby-name">{s.name}</span>
        <span className="cc-cubby-id">{s.id}</span>
        <span className="cc-cubby-tag">{tag}</span>
      </button>
    );
  };

  const groupedCfg = GROUPED_STATIONS[LOC_BY_KEY[selected].station];

  return (
    <div className="cc-root">
      <style>{css}</style>

      <header className="cc-head">
        <div className="cc-title">
          <h1>Cubby Check-In</h1>
          <p>King's Academy · Manual reconciliation</p>
        </div>
        <div className="cc-meta">
          <label>Operator<input value={operator} onChange={(e) => setOperator(e.target.value)} placeholder="your name" /></label>
          <span className="cc-date">{new Date().toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" })}</span>
        </div>
      </header>

      {feedback && (
        <div className="cc-feedback" style={{ borderColor: feedback.kind === "ok" ? "#2e7d32" : RED, color: feedback.kind === "ok" ? "#2e7d32" : RED }}>
          {feedback.text}
        </div>
      )}

      <div className="cc-locs">
        {LOCATIONS.map((l) => {
          const total = (byLocation[l.key] || []).filter((s) => !s.exempt).length;
          const done = !!locState.committed[l.key];
          const rec = locState.recorded[l.key] || 0;
          return (
            <button
              key={l.key}
              className={`cc-loc ${selected === l.key ? "on" : ""} ${done ? "done" : ""}`}
              onClick={() => setSelected(l.key)}
            >
              <span className="cc-loc-name">{l.label}</span>
              <span className="cc-loc-sub">{total}{done ? " · done" : rec ? ` · ${rec}` : ""}</span>
            </button>
          );
        })}
      </div>

      <div className="cc-panel">
        <div className="cc-panel-head">
          <h2>{LOC_BY_KEY[selected].label}</h2>
          <div className="cc-panel-stats">
            <span style={{ color: "#2e7d32" }}><b>{presentCount}</b> in</span>
            <span style={{ color: RED }}><b>{missingCount}</b> missing</span>
            {exemptCount > 0 && <span style={{ color: "#8a8f98" }}><b>{exemptCount}</b> exempt</span>}
            <span><b>{checkable.length}</b> total</span>
          </div>
        </div>

        <p className="cc-hint">
          Tap a cubby to mark its phone as missing. Anything left unmarked is recorded as turned in when you commit.
          {exemptCount > 0 && " Greyed cubbies are phone-exempt and are not recorded."}
        </p>

        {list.length === 0 ? (
          <p className="cc-empty">No students have a cubby at this location yet.</p>
        ) : groupedCfg ? (
          (() => {
            const cfg = groupedCfg;
            const inGroup = (g) =>
              cfg.kind === "rooms"
                ? list.filter((s) => g.rooms.includes(s.room))
                : list.filter((s) => {
                    const n = Number(s.room);
                    return !isNaN(n) && n >= g.min && n <= g.max;
                  });
            const assigned = new Set();
            const sections = cfg.groups.map((g) => {
              const members = inGroup(g);
              members.forEach((s) => assigned.add(s.id));
              return { label: g.label, members };
            });
            const leftover = list.filter((s) => !assigned.has(s.id));
            return (
              <div className="cc-groups">
                {sections.map((sec) =>
                  sec.members.length === 0 ? null : (
                    <div key={sec.label} className="cc-group">
                      <div className="cc-group-label">{sec.label}</div>
                      <div className="cc-grid">{sec.members.map(renderCubby)}</div>
                    </div>
                  )
                )}
                {leftover.length > 0 && (
                  <div className="cc-group">
                    <div className="cc-group-label">Unassigned</div>
                    <div className="cc-grid">{leftover.map(renderCubby)}</div>
                  </div>
                )}
              </div>
            );
          })()
        ) : (
          <div className="cc-grid">{list.map(renderCubby)}</div>
        )}
      </div>

      {/* Sticky action bar — always reachable on a phone without scrolling up. */}
      <div className="cc-actionbar">
        <div className="cc-actionbar-stats">
          <span style={{ color: "#2e7d32" }}><b>{presentCount}</b> in</span>
          <span style={{ color: RED }}><b>{missingCount}</b> missing</span>
        </div>
        <div className="cc-actionbar-btns">
          <button className="ghost" onClick={clearLocation} disabled={checkable.length === 0}>Clear</button>
          <button className="commit" onClick={commit} disabled={checkable.length === 0}>Commit {LOC_BY_KEY[selected].label}</button>
        </div>
      </div>
    </div>
  );
}

const css = `
.cc-root{font-family:Calibri,'Segoe UI',system-ui,sans-serif;color:#1f2733;background:${BEIGE};padding:16px;max-width:1100px;margin:0 auto;padding-bottom:96px;}
.cc-root *{box-sizing:border-box;}
.cc-head{display:flex;justify-content:space-between;align-items:flex-start;gap:12px;border-bottom:3px solid ${NAVY};padding-bottom:10px;flex-wrap:wrap;}
.cc-head h1{font-family:Georgia,serif;color:${NAVY};margin:0;font-size:24px;}
.cc-head p{margin:2px 0 0;color:${GOLD};font-size:12px;letter-spacing:.06em;text-transform:uppercase;}
.cc-meta{display:flex;gap:10px;align-items:flex-end;flex-wrap:wrap;justify-content:flex-end;}
.cc-meta label{font-size:12px;color:#5b6472;display:flex;flex-direction:column;gap:3px;}
.cc-meta input{font-size:16px;padding:8px 8px;border:1px solid #ccc;border-radius:6px;min-width:150px;}
.cc-date{font-size:13px;color:${NAVY};font-weight:600;align-self:center;}
.cc-feedback{border:2px solid;border-radius:8px;padding:10px 14px;margin:12px 0;font-size:15px;font-weight:600;background:#fff;}

/* Location picker: wraps on desktop, horizontal scroll strip on phones. */
.cc-locs{display:grid;grid-template-columns:repeat(auto-fill,minmax(150px,1fr));gap:8px;margin:14px 0;}
.cc-loc{text-align:left;padding:10px 12px;border:1px solid #e2ddd0;background:#fff;border-radius:8px;cursor:pointer;display:flex;flex-direction:column;gap:2px;}
.cc-loc.on{border-color:${NAVY};box-shadow:0 0 0 2px rgba(35,60,95,.15);}
.cc-loc.done{background:#eef5ef;border-color:#2e7d32;}
.cc-loc-name{font-weight:600;color:${NAVY};font-size:15px;}
.cc-loc-sub{font-size:12px;color:#8a8f98;}

.cc-panel{background:#fff;border:1px solid #e2ddd0;border-radius:10px;padding:14px 16px;}
.cc-panel-head{display:flex;justify-content:space-between;align-items:center;gap:12px;flex-wrap:wrap;}
.cc-panel-head h2{font-family:Georgia,serif;color:${NAVY};margin:0;font-size:20px;}
.cc-panel-stats{display:flex;gap:14px;font-size:14px;color:#5b6472;}
.cc-panel-stats b{font-size:18px;}
.cc-hint{font-size:13px;color:#5b6472;margin:10px 0 14px;}
.cc-empty{color:#8a8f98;font-size:14px;}

.cc-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(150px,1fr));gap:10px;}
.cc-groups{display:flex;flex-direction:column;gap:16px;}
.cc-group{border:1px solid #ece7db;border-radius:10px;padding:12px;background:#fcfaf6;}
.cc-group-label{font-family:Georgia,serif;color:${NAVY};font-size:14px;font-weight:700;text-transform:uppercase;letter-spacing:.06em;margin-bottom:10px;}

.cc-cubby{text-align:left;background:#f0f7f1;border:2px solid #cfe4d4;border-radius:10px;padding:12px;cursor:pointer;display:flex;flex-direction:column;gap:2px;min-height:88px;}
.cc-cubby.missing{background:#fdeceb;border-color:${RED};}
.cc-cubby-code{font-family:Georgia,serif;font-weight:700;color:${NAVY};font-size:20px;}
.cc-cubby-name{font-size:12.5px;color:#5b6472;line-height:1.2;}
.cc-cubby-id{font-size:11px;color:#a2a7af;}
.cc-cubby-tag{margin-top:4px;font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.05em;color:#2e7d32;}
.cc-cubby.missing .cc-cubby-tag{color:${RED};}

/* Phone-exempt: grey, flat, not clickable. */
.cc-cubby.exempt,.cc-cubby.exempt:disabled{background:#ececec;border-color:#d4d4d4;border-style:dashed;cursor:not-allowed;opacity:.75;}
.cc-cubby.exempt .cc-cubby-code{color:#8a8f98;}
.cc-cubby.exempt .cc-cubby-name{color:#8a8f98;}
.cc-cubby.exempt .cc-cubby-tag{color:#8a8f98;}

/* Sticky bottom action bar */
.cc-actionbar{position:fixed;left:0;right:0;bottom:0;background:#fff;border-top:1px solid #e2ddd0;box-shadow:0 -2px 8px rgba(0,0,0,.06);padding:10px 16px;display:flex;align-items:center;justify-content:space-between;gap:12px;z-index:50;}
.cc-actionbar-stats{display:flex;gap:14px;font-size:14px;color:#5b6472;}
.cc-actionbar-stats b{font-size:16px;}
.cc-actionbar-btns{display:flex;gap:8px;}
.cc-actionbar-btns button{font-size:15px;padding:11px 16px;border-radius:8px;cursor:pointer;border:1px solid ${NAVY};background:#fff;color:${NAVY};font-weight:600;}
.cc-actionbar-btns .commit{background:${NAVY};color:#fff;}
.cc-actionbar-btns .ghost{background:#eef0f2;border-color:#eef0f2;color:#5b6472;}
.cc-actionbar-btns button:disabled{opacity:.5;cursor:not-allowed;}

/* ---------- Phone layout ---------- */
@media(max-width:640px){
  .cc-root{padding:12px;padding-bottom:88px;}
  .cc-head{flex-direction:column;}
  .cc-title h1{font-size:21px;}
  .cc-meta{justify-content:flex-start;width:100%;}
  .cc-meta input{min-width:0;width:100%;}
  .cc-meta label{flex:1;}

  /* Locations become a horizontal scroll strip so cubbies stay near the top. */
  .cc-locs{display:flex;overflow-x:auto;gap:8px;padding-bottom:4px;-webkit-overflow-scrolling:touch;grid-template-columns:none;}
  .cc-loc{flex:0 0 auto;min-width:120px;}

  /* Two comfortable columns of cubbies. */
  .cc-grid{grid-template-columns:repeat(2,1fr);gap:8px;}
  .cc-cubby{min-height:80px;padding:10px;}
  .cc-cubby-code{font-size:18px;}

  .cc-panel-head h2{font-size:18px;}
  .cc-actionbar-btns .commit{padding:11px 14px;}
}
@media(max-width:360px){
  .cc-grid{grid-template-columns:1fr;}
}
`;